import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils/phoneUtils';
import {
    useUpdateArchitectContactInfo,
    useUpdateSubcontractorContactInfo,
    useGetArchitect,
    useGetSubcontractor
} from '@/hooks/use-contractors';
import { useGetRoles } from '@/hooks/use-company';
import { Role } from '@/types/api';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';

// Form validation schema
const formSchema = z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    emailAddress: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
    phoneNumber: z
        .string()
        .min(1, 'Phone number is required')
        .refine(
            (phone) => validatePhoneNumber(phone),
            'Phone number must be in format (555) 123-4567'
        ),
    roleInCompany: z.string().min(1, 'Role in company is required'),
});

type FormValues = z.infer<typeof formSchema>;

export default function ContactInfoStep() {
    const [searchParams] = useSearchParams();
    const { handleError } = useErrorHandler();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // Fetch data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    const isLoadingData = isLoadingArchitect || isLoadingSubcontractor;
    const currentData = accountType === 'architect' ? architectData : subcontractorData;

    // API hooks for roles and updates
    const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useGetRoles();
    const updateArchitectContactInfo = useUpdateArchitectContactInfo();
    const updateSubcontractorContactInfo = useUpdateSubcontractorContactInfo();

    // Extract roles from API response structure: { data: Role[], meta: {...} }
    const roles = rolesData?.data || [];

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            emailAddress: '',
            phoneNumber: '',
            roleInCompany: '',
        },
    });

    // Handle missing orgId
    useEffect(() => {
        if (!organizationId) {
            toast({
                title: 'Missing Organization ID',
                description: 'Please complete the signup process first.',
                variant: 'warning',
            });
        }
    }, [organizationId]);

    // Prefill form with existing data
    useEffect(() => {
        if (!currentData) return;

        const specificProfile = accountType === 'architect'
            ? (currentData as any).architectSpecificProfile
            : (currentData as any).subcontractorSpecificProfile;

        if (specificProfile && (specificProfile.primaryContactFirstName || specificProfile.primaryContactLastName || specificProfile.primaryContactEmail)) {
            form.reset({
                firstName: specificProfile.primaryContactFirstName || '',
                lastName: specificProfile.primaryContactLastName || '',
                emailAddress: specificProfile.primaryContactEmail || '',
                phoneNumber: specificProfile.primaryContactPhone || '',
                roleInCompany: specificProfile.primaryContactRole || '',
            });
        }
    }, [currentData, form, accountType]);

    const handleNext = async (data: FormValues) => {
        console.log('ContactInfoStep: handleNext called with data:', data);
        console.log('ContactInfoStep: organizationId:', organizationId, 'accountType:', accountType);

        if (!organizationId) {
            console.log('ContactInfoStep: No organizationId - allowing to proceed anyway');
            toast({
                title: 'Warning',
                description: 'Organization ID is missing but proceeding...',
                variant: 'warning',
            });
            return true; // Allow navigation to continue
        }

        if (!accountType || (accountType !== 'architect' && accountType !== 'subcontractor')) {
            console.log('ContactInfoStep: Invalid account type:', accountType);
            toast({
                title: 'Error',
                description: 'Invalid account type. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        setIsSubmitting(true);
        try {
            // Prepare the payload
            const payload = {
                firstName: data.firstName,
                lastName: data.lastName,
                emailAddress: data.emailAddress,
                phoneNumber: data.phoneNumber,
                roleInCompany: data.roleInCompany, // This is the role ID
            };

            // Call the appropriate API based on account type
            if (accountType === 'architect') {
                await updateArchitectContactInfo.mutateAsync({
                    architectId: organizationId,
                    data: payload,
                });
            } else if (accountType === 'subcontractor') {
                await updateSubcontractorContactInfo.mutateAsync({
                    subcontractorId: organizationId,
                    data: payload,
                });
            }

            toast({
                title: 'Contact Info Saved',
                description: 'Your contact information has been saved successfully.',
                variant: 'success',
            });

            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to save contact information. Please try again.',
            });
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OnboardingWrapper
            currentStep={3}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                console.log('ContactInfoStep: onNext called');
                const isValid = await form.trigger();
                console.log('ContactInfoStep: Form validation result:', isValid);

                if (!isValid) {
                    console.log('ContactInfoStep: Form validation failed');
                    return false;
                }

                return new Promise((resolve) => {
                    console.log('ContactInfoStep: Submitting form...');
                    form.handleSubmit(async (data) => {
                        console.log('ContactInfoStep: Form submitted with data:', data);
                        const result = await handleNext(data);
                        console.log('ContactInfoStep: handleNext result:', result);
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting}
            saveButtonText="Save & Continue"
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Primary Contact Information
                    </h2>
                    <p className="text-muted-foreground">
                        Please provide the contact details for the primary point of contact at your company. This person will receive important communications about your account.
                    </p>
                </div>

                <Form {...form}>
                    <div className="space-y-6">
                        {/* Show loading state while fetching existing data */}
                        {isLoadingData && (
                            <div className="flex items-center justify-center py-4">
                                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                                <span className="ml-2 text-muted-foreground">
                                    Loading existing data...
                                </span>
                            </div>
                        )}

                        {/* Name Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            First Name<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter first name"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Last Name<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter last name"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Contact Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="emailAddress"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Email Address<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder="example@company.com"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="phoneNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Phone Number<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="(555) 123-4567"
                                                {...field}
                                                className="mt-1"
                                                onChange={(e) => {
                                                    const formatted = formatPhoneNumber(e.target.value);
                                                    field.onChange(formatted);
                                                }}
                                                maxLength={14}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Role in Company */}
                        <FormField
                            control={form.control}
                            name="roleInCompany"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-foreground font-medium">
                                        Role in Company<span className="text-red-600">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                            disabled={rolesLoading || !!rolesError}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={
                                                    rolesLoading ? "Loading roles..." :
                                                        rolesError ? "Error loading roles" :
                                                            "Select your role"
                                                } />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {rolesLoading && (
                                                    <div className="p-2 text-sm text-muted-foreground">
                                                        Loading roles...
                                                    </div>
                                                )}
                                                {rolesError && (
                                                    <div className="p-2 text-sm text-red-600">
                                                        Failed to load roles. Please refresh the page and try again.
                                                    </div>
                                                )}
                                                {!rolesLoading && !rolesError && roles?.map((role: Role) => (
                                                    <SelectItem key={role.id} value={role.id}>
                                                        {role.name}
                                                    </SelectItem>
                                                ))}
                                                {!rolesLoading && !rolesError && (!roles || roles.length === 0) && (
                                                    <div className="p-2 text-sm text-muted-foreground">
                                                        No roles available
                                                    </div>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Note Box */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                <span className="font-medium">Note:</span> This contact information will be used for all official communications regarding your partnership with LarkPM, including account updates, billing notifications, and important announcements.
                            </p>
                        </div>
                    </div>
                </Form>
            </div>
        </OnboardingWrapper>
    );
}
